/* eslint-disable camelcase */
import { ModelMixin } from '@app/models/core/base-generic.model';
import { KeysToCamelCase } from '@app/types/camel-case.type';
import { Province } from '@app/types/province';
import { formatLocalizedNumber, parseLocalizedNumber } from '@app/types/translatable.type';
export class TaxCredit extends ModelMixin<TaxCredit, string>() {
    protected static _resource = 'payroll/employees/:employee/personalTaxCreditsAndDeductions';

    protected static _version = 'v2';

    protected static _type = 'personal_tax_credit_and_deductions';

    protected static _serializeAttributes = ['jurisdiction', 'year', 'fields', 'state'];

    set jurisdiction(jurisdiction: Province | 'Canada') {
        this._attributes['jurisdiction'] = jurisdiction;
    }

    get jurisdiction(): Province | 'Canada' {
        return this._attributes['jurisdiction'];
    }

    set year(year: string) {
        this._attributes['year'] = `${year}-01-01`;
    }

    get year(): string {
        return this._attributes['year']?.split('-')[0];
    }

    set state(state: 'active' | 'inactive') {
        this._attributes['state'] = state;
    }

    get state(): 'active' | 'inactive' {
        return this._attributes['state'];
    }

    get createdAt(): Date {
        return this._attributes['createdAt'];
    }

    set alimonyPayments(alimonyPayments: string) {
        this.setFields('f2_alimony', alimonyPayments);
    }

    get alimonyPayments(): string | undefined {
        return this.formatNumber(this.fields.f2Alimony);
    }

    set annualDeductions(annualDeductions: string) {
        this.setFields('f1_annual_deductions', annualDeductions);
    }

    get annualDeductions(): string | undefined {
        return this.formatNumber(this.fields.f1AnnualDeductions);
    }

    set federalAdditionalDeductions(federalAdditionalDeductions: string) {
        this.setFields('additional_tax_deductions', federalAdditionalDeductions);
    }

    get federalAdditionalDeductions(): string | undefined {
        return this.formatNumber(this.fields.additionalTaxDeductions);
    }

    set quebecAdditionalDeductions(quebecAdditionalDeductions: string) {
        this.setFields('tp_1015_line_11_additional_source_deductions', quebecAdditionalDeductions);
    }

    get quebecAdditionalDeductions(): string | undefined {
        return this.formatNumber(this.fields.tp1015Line11AdditionalSourceDeductions);
    }

    set deductionForZone(deductionForZone: string) {
        this.setFields('deduction_for_zone', deductionForZone);
    }

    get deductionForZone(): string | undefined {
        return this.formatNumber(this.fields.deductionForZone);
    }

    set claimAmount(claimAmount: string) {
        const parsedClaimAmount = this.parseCurrency(claimAmount);
        if (this.jurisdiction === 'Canada') {
            this.setFields('federal_personal_amount', parsedClaimAmount);
        } else {
            this.setFields('provincial_personal_amount', parsedClaimAmount);
        }
    }

    get claimAmount(): string | undefined {
        if (this.jurisdiction === 'Canada') {
            return this.formatNumber(this.fields.federalPersonalAmount);
        }
        return this.formatNumber(this.fields.provincialPersonalAmount);
    }

    get otherFederal(): string | undefined {
        return this.formatNumber(this.fields.k3OtherFederalDeductions);
    }

    set otherFederal(otherFederal: string) {
        this.setFields('k3_other_federal_deductions', otherFederal);
    }

    get otherProvincial(): string | undefined {
        return this.formatNumber(this.fields.k3pOtherProvincialDeductions);
    }

    set otherProvincial(otherProvincial: string) {
        this.setFields('k3p_other_provincial_deductions', otherProvincial);
    }

    get personalTax(): string | undefined {
        return this.formatNumber(this.fields.tp1015Line7IndexedValueOfPersonalTaxCredits);
    }

    set personalTax(personalTax: string) {
        this.setFields('tp_1015_line_7_indexed_value_of_personal_tax_credits', personalTax);
    }

    get careerExtension(): string | undefined {
        return this.formatNumber(this.fields.tp1015Line9NonIndexedValueOfPersonalTaxCredits);
    }

    set careerExtension(careerExtension: string) {
        this.setFields('tp_1015_line_9_non_indexed_value_of_personal_tax_credits', careerExtension);
    }

    get deductions(): string | undefined {
        return this.formatNumber(this.fields.tp1015Line19Deductions);
    }

    set deductions(deductions: string) {
        this.setFields('tp_1015_line_19_deductions', deductions);
    }

    private setFields(fieldName: keyof AdditionalFields, value: string | number): void {
        this._attributes['fields'] = { ...this.fields, [fieldName]: this.parseCurrency(value) };
    }

    private formatNumber(value: number | undefined | null): string | undefined {
        return formatLocalizedNumber(value, { locale: this.locale, decimalPlaces: 2 });
    }

    // Keys are converted to camelCase for getters due to the HR API middleware
    private get fields(): KeysToCamelCase<AdditionalFields> {
        return this._attributes['fields'] ?? {};
    }

    private parseCurrency(currency: string | number): number {
        return parseLocalizedNumber(currency, this.locale, false);
    }
}

/*
 ! These keys are written in snake_case on purpose! The HR middleware's automatic conversion does not work for these due to the digits in them
 ie. it saves tp1015Line19Deductions as "tp1015_line19_deductions" when it should be "tp_1015_line_19_deductions"
 This is an issue because these fields need to be exact.

 they are automatically converted again to camelCase when returning from the HR API. This leads to a bit of uneven accessing and modifying.
to GET an attribute we use camelCase
to SET an attribute we use snake_case
*/
type AdditionalFields = { [key: string]: number } & {
    federal_personal_amount: number;
    provincial_personal_amount: number;
    deduction_for_zone: number;
    additional_tax_deductions: number;
    f1_annual_deductions: number;
    f2_alimony: number;
    k3_other_federal_deductions: number;
    k3p_other_provincial_deductions: number;
    tp_1015_line_19_deductions: number;
    tp_1015_line_11_additional_source_deductions: number;
    tp_1015_line_7_indexed_value_of_personal_tax_credits: number;
    tp_1015_line_9_non_indexed_value_of_personal_tax_credits: number;
};
